import { JobCardForm, SERVICE_TYPES } from "@/pages/jobcards/types";

interface Props {
  form: JobCardForm;
  onChange: (patch: Partial<JobCardForm>) => void;
}

export default function SectionProjectServices({ form, onChange }: Props) {
  const toggleService = (label: string) => {
    const current = form.services;
    if (current.includes(label)) {
      onChange({ services: current.filter((s) => s !== label) });
    } else {
      onChange({ services: [...current, label] });
    }
  };

  return (
    <>
      {/* Section 5 — Project Title & Location */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-map-pin-line text-amber-600 text-base" />
          </span>
          <h3 className="text-sm font-semibold text-gray-800">Section 5 — Project Title &amp; Survey Location</h3>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Project Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.projectTitle}
              onChange={(e) => onChange({ projectTitle: e.target.value })}
              placeholder="e.g. Topographic Survey - Shamshabad Industrial Zone"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Survey Location <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                <i className="ri-map-pin-2-line text-gray-400 text-sm" />
              </span>
              <input
                type="text"
                value={form.surveyLocation}
                onChange={(e) => onChange({ surveyLocation: e.target.value })}
                placeholder="e.g. Shamshabad, Hyderabad, Telangana"
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Survey Area / Extent</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                <i className="ri-landscape-line text-gray-400 text-sm" />
              </span>
              <input
                type="text"
                value={form.area}
                onChange={(e) => onChange({ area: e.target.value })}
                placeholder="e.g. 125 Acres / 42 km stretch"
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Priority</label>
            <div className="flex items-center gap-2 flex-wrap">
              {["Low", "Medium", "High", "Critical"].map((p) => {
                const colors: Record<string, string> = {
                  Low: "border-amber-300 bg-amber-50 text-amber-700",
                  Medium: "border-blue-300 bg-blue-50 text-blue-700",
                  High: "border-amber-300 bg-amber-50 text-amber-700",
                  Critical: "border-red-300 bg-red-50 text-red-700",
                };
                const active = form.priority === p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => onChange({ priority: p })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                      active ? colors[p] : "border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Section 6 — Service Types */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-tools-line text-amber-600 text-base" />
          </span>
          <h3 className="text-sm font-semibold text-gray-800">Section 6 — Service Type</h3>
          {form.services.length > 0 && (
            <span className="ml-auto text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full font-semibold">
              {form.services.length} selected
            </span>
          )}
        </div>
        <div className="p-5">
          <p className="text-xs text-gray-500 mb-4">Select all applicable service types for this job card</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
            {SERVICE_TYPES.map((s) => {
              const selected = form.services.includes(s.label);
              return (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => toggleService(s.label)}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 text-center transition-all cursor-pointer ${
                    selected
                      ? "border-amber-400 bg-amber-50 text-amber-700"
                      : "border-gray-100 bg-gray-50 text-gray-600 hover:border-gray-200 hover:bg-white"
                  }`}
                >
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: selected ? "#d1fae5" : "#f3f4f6" }}>
                    <i className={`${s.icon} text-lg`} style={{ color: selected ? "#059669" : "#9ca3af" }} />
                  </span>
                  <span className="text-xs font-medium leading-tight">{s.label}</span>
                  {selected && (
                    <span className="w-4 h-4 flex items-center justify-center rounded-full bg-amber-500 flex-shrink-0">
                      <i className="ri-check-line text-white text-xs" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Custom Service input */}
          {form.services.includes("Custom Service") && (
            <div className="mt-4">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Custom Service Description</label>
              <input
                type="text"
                value={form.customService}
                onChange={(e) => onChange({ customService: e.target.value })}
                placeholder="Describe the custom service..."
                className="w-full px-3 py-2.5 border border-amber-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 bg-amber-50/30 transition-all"
              />
            </div>
          )}

          {/* Selected chips */}
          {form.services.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {form.services.map((s) => (
                <span key={s} className="flex items-center gap-1.5 text-xs bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full font-medium">
                  {s}
                  <button
                    type="button"
                    onClick={() => toggleService(s)}
                    className="w-3 h-3 flex items-center justify-center hover:text-red-500 cursor-pointer"
                  >
                    <i className="ri-close-line text-xs" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
